import { useCallback, MouseEvent, useMemo } from 'react';
import { Stack } from '@mui/material';

import { TableCell, TableRow, Button, Typography } from '@occc/ui-design-system';
import { StatusIndicator, TrendBadgeDelta } from '@occc/ui-design-system';
import {
  RetailerNetworkScanDataLatestSubmissionsFragment,
  ScanDataWeeklySubmissionStatus,
} from '@occc/graphql';

import {
  formatInt,
  formatUsdCents,
  formatSubmittedAt,
  getScanDataSubmissionStatusParams,
} from '../../../retailer-network-tobacco-scan-data-page/components/tobacco-submissions.utils';

type TobaccoSubmissionsTableRowProps = {
  item: RetailerNetworkScanDataLatestSubmissionsFragment;
  onViewAllStoresClick: (submission: RetailerNetworkScanDataLatestSubmissionsFragment) => void;
};

export const TobaccoSubmissionsTableRow = ({
  item,
  onViewAllStoresClick,
}: TobaccoSubmissionsTableRowProps) => {
  const estimatedReimbursement =
    item.estimatedReimbursements.multiUnit + item.estimatedReimbursements.loyalty;

  const prevEstimatedReimbursement =
    item.previousEstimatedReimbursements.multiUnit + item.previousEstimatedReimbursements.loyalty;

  const statusParams = useMemo(() => getScanDataSubmissionStatusParams(item.status), [item.status]);

  const isViewAllButtonVisible =
    item.totalSitesSubmitted > 0 && item.status !== ScanDataWeeklySubmissionStatus.NotSubmitted;
  const onViewAllStores = useCallback(
    (event: MouseEvent<unknown>) => {
      event.stopPropagation();
      onViewAllStoresClick(item);
    },
    [item, onViewAllStoresClick],
  );

  return (
    <TableRow hover>
      <TableCell>{formatSubmittedAt(item.submittedAt)}</TableCell>
      <TableCell>
        <StatusIndicator color={statusParams.color} label={statusParams.label} />
      </TableCell>
      <TableCell>
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography variant='body2'>{formatInt(item.totalSitesSubmitted)}</Typography>
          {isViewAllButtonVisible ? (
            <Button
              variant='text'
              size='small'
              sx={{ textTransform: 'none' }}
              onClick={onViewAllStores}
            >
              View All
            </Button>
          ) : null}
        </Stack>
      </TableCell>
      <TableCell>{formatInt(item.totalMultiUnitTransactions)}</TableCell>
      <TableCell>{formatInt(item.totalLoyaltyTransactions)}</TableCell>
      <TableCell align='right'>
        <Stack direction='row' alignItems='center' justifyContent='flex-end' gap={1}>
          <TrendBadgeDelta value={estimatedReimbursement} prevValue={prevEstimatedReimbursement} />
          <Typography variant='body2'>{formatUsdCents(estimatedReimbursement)}</Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );
};
